import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';

import { 
  DynamicExportConfig, 
  DynamicReportRequest,
  fetchReportData,
  validateDynamicExportConfig 
} from '../../../utils/exportUtils';

interface ExportPreviewProps {
  config: DynamicExportConfig;
  previewData: any[];
  onPreviewDataChange: (data: any[]) => void;
  onConfigChange: (config: DynamicExportConfig) => void;
}

export const ExportPreview: React.FC<ExportPreviewProps> = ({
  config,
  previewData,
  onPreviewDataChange,
  onConfigChange
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const orientationOptions = [
    { label: 'Paisagem (Landscape)', value: 'landscape' },
    { label: 'Retrato (Portrait)', value: 'portrait' }
  ];

  useEffect(() => {
    if (config.selectedColumns.length > 0) {
      loadPreviewData();
    }
  }, [config.selectedColumns, config.whereConditions, config.tableInfo.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPreviewData = async () => {
    try {
      setLoading(true);
      setError(null);

      const reportRequest: DynamicReportRequest = {
        table: config.tableInfo.name,
        columns: config.selectedColumns.map(col => col.name),
        where: config.whereConditions
      };

      const data = await fetchReportData(reportRequest);
      
      // Limitar preview a 10 registros para performance
      const previewSample = data.slice(0, 10);
      setTotalRecords(data.length);
      onPreviewDataChange(previewSample);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar preview';
      setError(errorMessage);
      onPreviewDataChange([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field: keyof DynamicExportConfig, value: any) => {
    onConfigChange({
      ...config,
      [field]: value
    });
  };

  const renderPreviewTable = () => {
    if (loading) {
      return (
        <div className="text-center p-6">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          <p className="mt-3">Carregando preview dos dados...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Message 
          severity="error" 
          text={error}
          className="w-full"
        />
      );
    }

    if (previewData.length === 0) {
      return (
        <Message 
          severity="warn" 
          text="Nenhum dado encontrado com os filtros aplicados"
          className="w-full"
        />
      );
    }

    return (
      <div>
        <div className="flex justify-content-between align-items-center mb-3">
          <div className="flex align-items-center gap-2">
            <Tag severity="info" value={`${previewData.length} de ${totalRecords} registros`} />
            {totalRecords > 10 && (
              <small className="text-color-secondary">
                (Mostrando apenas os primeiros 10 para preview)
              </small>
            )}
          </div>
          <Button
            label="Atualizar Preview"
            icon="pi pi-refresh"
            size="small"
            outlined
            onClick={loadPreviewData}
          />
        </div>

        <DataTable 
          value={previewData} 
          scrollable 
          scrollHeight="300px"
          className="p-datatable-sm"
        >
          {config.selectedColumns.map(column => (
            <Column
              key={column.name}
              field={column.name}
              header={column.displayName || column.name}
              style={{ minWidth: '150px' }}
              body={(rowData) => {
                const value = rowData[column.name];
                
                // Formatação especial por tipo
                if (value === null || value === undefined) {
                  return <span className="text-color-secondary">-</span>;
                }
                
                if (column.type === 'date' || column.type === 'datetime') {
                  try {
                    return new Date(value).toLocaleDateString('pt-PT');
                  } catch {
                    return value;
                  }
                }
                
                if (typeof value === 'boolean') {
                  return value ? 
                    <Tag severity="success" value="Sim" /> : 
                    <Tag severity="danger" value="Não" />;
                }
                
                if (typeof value === 'string' && value.length > 50) {
                  return (
                    <span title={value}>
                      {value.substring(0, 47)}...
                    </span>
                  );
                }
                
                return value;
              }}
            />
          ))}
        </DataTable>
      </div>
    );
  };

  const renderValidationSummary = () => {
    const validation = validateDynamicExportConfig(config);
    
    if (validation.valid) {
      return (
        <Message 
          severity="success" 
          text="Configuração válida - Pronto para exportar!"
          className="w-full"
        />
      );
    }

    return (
      <Message 
        severity="error" 
        className="w-full"
        content={
          <div>
            <p><strong>Problemas encontrados:</strong></p>
            <ul className="mt-2 mb-0">
              {validation.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        }
      />
    );
  };

  return (
    <div className="grid">
      <div className="col-12">
        <Card title="Configurações de Exportação" className="mb-3">
          <div className="grid">
            <div className="col-12 md:col-4">
              <label className="block text-sm font-semibold mb-2">Nome do Arquivo</label>
              <InputText
                value={config.filename}
                onChange={(e) => handleConfigChange('filename', e.target.value)}
                placeholder="nome_do_arquivo"
                className="w-full"
              />
            </div>

            <div className="col-12 md:col-4">
              <label className="block text-sm font-semibold mb-2">Título do Relatório</label>
              <InputText
                value={config.title}
                onChange={(e) => handleConfigChange('title', e.target.value)}
                placeholder="Título do relatório"
                className="w-full"
              />
            </div>

            <div className="col-12 md:col-4">
              <label className="block text-sm font-semibold mb-2">Orientação (PDF)</label>
              <Dropdown
                value={config.orientation}
                options={orientationOptions}
                onChange={(e) => handleConfigChange('orientation', e.value)}
                className="w-full"
              />
            </div>

            <div className="col-12 md:col-6">
              <label className="block text-sm font-semibold mb-2">Subtítulo (Opcional)</label>
              <InputText
                value={config.subtitle || ''}
                onChange={(e) => handleConfigChange('subtitle', e.target.value)}
                placeholder="Subtítulo do relatório"
                className="w-full"
              />
            </div>

            <div className="col-12 md:col-6 flex align-items-end">
              <div className="flex align-items-center">
                <Checkbox
                  checked={config.showPageNumbers || false}
                  onChange={(e) => handleConfigChange('showPageNumbers', e.checked)}
                />
                <label className="ml-2">Mostrar numeração de páginas (PDF)</label>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="col-12">
        <Card title="Resumo da Exportação" className="mb-3">
          <div className="grid">
            <div className="col-12 md:col-3">
              <div className="text-center p-3 bg-primary-50 border-round">
                <i className="pi pi-table text-primary text-2xl mb-2 block" />
                <div className="text-xl font-bold text-primary">
                  {config.tableInfo.displayName || config.tableInfo.name}
                </div>
                <small className="text-color-secondary">Tabela</small>
              </div>
            </div>

            <div className="col-12 md:col-3">
              <div className="text-center p-3 bg-orange-50 border-round">
                <i className="pi pi-list text-orange-500 text-2xl mb-2 block" />
                <div className="text-xl font-bold text-orange-500">
                  {config.selectedColumns.length}
                </div>
                <small className="text-color-secondary">Colunas</small>
              </div>
            </div>

            <div className="col-12 md:col-3">
              <div className="text-center p-3 bg-purple-50 border-round">
                <i className="pi pi-filter text-purple-500 text-2xl mb-2 block" />
                <div className="text-xl font-bold text-purple-500">
                  {config.whereConditions?.length || 0}
                </div>
                <small className="text-color-secondary">Filtros</small>
              </div>
            </div>

            <div className="col-12 md:col-3">
              <div className="text-center p-3 bg-green-50 border-round">
                <i className="pi pi-database text-green-500 text-2xl mb-2 block" />
                <div className="text-xl font-bold text-green-500">
                  {loading ? '...' : totalRecords}
                </div>
                <small className="text-color-secondary">Registros</small>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="col-12">
        <Card title="Preview dos Dados">
          {renderPreviewTable()}
        </Card>
      </div>

      <div className="col-12">
        <Card title="Validação" className="mt-3">
          {renderValidationSummary()}
        </Card>
      </div>
    </div>
  );
};